import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FileArrowUp, FileDoc, WarningCircle } from 'phosphor-react';
import { toast } from 'sonner';
import { useEventos } from '../../../../hooks/useEventos';
import {
  IMapaCampo,
  mensagemDoErro,
  useAcoesTemplate,
  useTarefa,
  useTemplateDoEvento,
} from '../../../../hooks/useEventoContrato';
import DialogModal from '../../../../components/DialogModal';
import SelectTipoContrato from './SelectTipoContrato';
import {
  ITemplateComTipo,
  ITipoContrato,
  TIPO_CONTRATO_ADITIVO,
  TIPO_CONTRATO_PADRAO,
  agruparPorTipo,
  comTipo,
} from './tiposDeContrato';

interface Props {
  eventoId: number;
  isOpen: boolean;
  onClose: () => void;
  /** Tipo do arquivo novo ao abrir: o do contrato, quando a gaveta abre pela tela dele. */
  tipoInicial?: string;
}

/** O trecho que muda, destacado dentro da frase do contrato. */
function Contexto({ campo }: { campo: IMapaCampo }) {
  const [antes, depois] = campo.contexto.split(campo.ancora.replace(/\n/g, ' '));
  return (
    <Text fontSize="11px" color="gray.500" noOfLines={2}>
      {antes}
      <Text as="mark" bg="yellow.100" color="gray.800" px="2px">
        {campo.ancora}
      </Text>
      {depois}
    </Text>
  );
}

function Template({
  t,
  eventoId,
  tipos,
}: {
  t: ITemplateComTipo;
  eventoId: number;
  tipos: ITipoContrato[];
}) {
  const { ler, confirmar, remover, trocarTipo, invalidar } = useAcoesTemplate(eventoId);
  const { acompanhar, rodando } = useTarefa((status, erro) => {
    if (status === 'failed') toast.error(erro || 'A leitura do template falhou.');
    return invalidar();
  });
  const removerTemplate = () =>
    remover.mutate(t.id, {
      onError: e => toast.error(mensagemDoErro(e, 'Não foi possível remover o template.')),
    });
  const { mapa } = t;
  const campos = mapa?.campos ?? [];
  // Tipo escolhido num template já lido: espera a confirmação (o select de teclado dispara a troca).
  const [tipoPedido, setTipoPedido] = useState<string | null>(null);
  const trocar = (contratoTipo: string) =>
    trocarTipo.mutate(
      { docId: t.id, contratoTipo },
      {
        // A leitura é por tipo: lido como outro tipo, o mapa sai e o template pede leitura nova.
        onSuccess: r => {
          if (r?.data?.ler_de_novo)
            toast.info('Tipo trocado. Leia o template de novo: a leitura é por tipo de contrato.');
        },
        onError: e => toast.error(mensagemDoErro(e, 'Não foi possível trocar o tipo do template.')),
      },
    );
  const [manter, setManter] = useState<Set<string>>(new Set());
  useEffect(() => setManter(new Set(campos.map(c => c.id))), [mapa?.status, campos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const lerDeNovo = async () => {
    try {
      acompanhar(await ler.mutateAsync(t.id));
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Não foi possível ler o template.'));
    }
  };
  const lendo = rodando || mapa?.status === 'lendo';
  // O modelo do termo aditivo não é lido: o robô de eventos preenche.
  const doRobo = t.contrato_tipo === TIPO_CONTRATO_ADITIVO;
  const grupos = Array.from(new Set(campos.map(c => c.grupo)));
  // O mesmo dado em mais de um lugar (qualificação e assinatura) é UM valor na ficha.
  const lugar = (c: IMapaCampo) => {
    const iguais = campos.filter(x => x.papel === c.papel);
    return iguais.length > 1 ? ` (${iguais.indexOf(c) + 1}º de ${iguais.length} lugares)` : '';
  };

  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="12px" p={4} mb={4}>
      <Flex align="center" gap={2} mb={2}>
        <FileDoc size={16} color="#3182CE" />
        <Text fontWeight={600} fontSize="14px" flex={1} noOfLines={1}>
          {t.nome}
        </Text>
        {lendo && (
          <Badge colorScheme="blue" textTransform="none">
            <Spinner size="xs" mr={1} /> lendo…
          </Badge>
        )}
        {!lendo && mapa?.status === 'pronto' && (
          <Badge colorScheme="orange" textTransform="none">
            confira e confirme
          </Badge>
        )}
        {!lendo && mapa?.status === 'confirmado' && (
          <Badge colorScheme="green" textTransform="none">
            confirmado
          </Badge>
        )}
        {!lendo && mapa?.status === 'erro' && (
          <Badge colorScheme="red" textTransform="none">
            erro na leitura
          </Badge>
        )}
      </Flex>
      <Flex align="center" gap={2} mb={2}>
        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">
          Gera o contrato de
        </Text>
        <SelectTipoContrato
          tipos={tipos}
          value={
            trocarTipo.isPending
              ? (trocarTipo.variables?.contratoTipo ?? t.contrato_tipo)
              : t.contrato_tipo
          }
          maxW="260px"
          aria-label={`Tipo de contrato do template ${t.nome}`}
          isDisabled={trocarTipo.isPending}
          onChange={contratoTipo => (mapa ? setTipoPedido(contratoTipo) : trocar(contratoTipo))}
        />
      </Flex>
      <DialogModal
        isOpen={tipoPedido !== null}
        status="warning"
        title="Trocar o tipo deste template?"
        description="A leitura do template é por tipo de contrato: trocar apaga o mapa lido e conferido, e o template precisa ser lido e confirmado de novo. Os contratos do tipo atual deixam de usar este arquivo."
        onConfirm={() => {
          const novo = tipoPedido;
          setTipoPedido(null);
          if (novo) trocar(novo);
        }}
        onCancel={() => setTipoPedido(null)}
        confirmLabel="Trocar o tipo"
        cancelLabel="Cancelar"
      />

      {doRobo && (
        <Flex align="center" gap={2}>
          <Text fontSize="12px" color="gray.600" flex={1}>
            O robô de eventos preenche este modelo em cada termo aditivo: não precisa ler nem
            confirmar.
          </Text>
          <Button
            size="sm"
            variant="ghost"
            colorScheme="red"
            isLoading={remover.isPending}
            onClick={removerTemplate}
          >
            Remover
          </Button>
        </Flex>
      )}
      {!doRobo && !mapa && !lendo && (
        <Button size="sm" colorScheme="blue" onClick={lerDeNovo}>
          Ler template
        </Button>
      )}
      {mapa?.status === 'erro' && !lendo && (
        <>
          <Text fontSize="12px" color="red.600" mb={2}>
            {mapa.erro}
          </Text>
          <Flex gap={2} wrap="wrap">
            <Button size="sm" colorScheme="blue" onClick={lerDeNovo} isLoading={ler.isPending}>
              Ler de novo
            </Button>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              isLoading={remover.isPending}
              onClick={removerTemplate}
            >
              Remover
            </Button>
          </Flex>
        </>
      )}

      {(mapa?.status === 'pronto' || mapa?.status === 'confirmado') && !lendo && (
        <>
          <Text fontSize="12px" fontWeight={600} color="gray.700" mt={2} mb={1}>
            Muda a cada fornecedor ({manter.size} de {campos.length})
          </Text>
          {grupos.map(g => (
            <Box key={g} mb={2}>
              <Text fontSize="11px" color="gray.400" textTransform="uppercase" mb={1}>
                {g}
              </Text>
              {campos
                .filter(c => c.grupo === g)
                .map(c => (
                  <Flex key={c.id} gap={2} align="flex-start" mb={1.5}>
                    <Checkbox
                      mt="2px"
                      isChecked={manter.has(c.id)}
                      onChange={e => {
                        const novo = new Set(manter);
                        if (e.target.checked) novo.add(c.id);
                        else novo.delete(c.id);
                        setManter(novo);
                      }}
                    />
                    <Box flex={1}>
                      <Text fontSize="12px" fontWeight={500}>
                        {c.rotulo}
                        {lugar(c)}
                      </Text>
                      <Contexto campo={c} />
                    </Box>
                  </Flex>
                ))}
            </Box>
          ))}
          {mapa.escopo_paragrafo != null && (
            <Text fontSize="12px" color="gray.600" mb={2}>
              A imagem do escopo do fornecedor anterior vira uma tabela com os itens do orçamento.
            </Text>
          )}
          {mapa.escopo_tabela != null && (
            <Text fontSize="12px" color="gray.600" mb={2}>
              As linhas de item da tabela de escopo do fornecedor anterior saem e voltam com os
              itens do orçamento.
            </Text>
          )}

          <Divider my={3} />
          <Text fontSize="12px" fontWeight={600} color="gray.700" mb={1}>
            Fica igual (texto do advogado da contratante)
          </Text>
          {mapa.fica?.contratante && (
            <Text fontSize="12px" color="gray.600">
              Contratante: {mapa.fica.contratante}
            </Text>
          )}
          {(mapa.fica?.testemunhas ?? []).length > 0 && (
            <Text fontSize="12px" color="gray.600">
              Testemunhas: {mapa.fica?.testemunhas?.join(', ')}
            </Text>
          )}
          {(mapa.fica?.terceiros ?? []).length > 0 && (
            <Text fontSize="12px" color="gray.600">
              Terceiros: {mapa.fica?.terceiros?.join(', ')}
            </Text>
          )}
          {mapa.evento?.data && (
            <Text fontSize="12px" color="gray.600">
              Evento no template:{' '}
              {[mapa.evento.data, mapa.evento.local].filter(Boolean).join(' · ')}
            </Text>
          )}

          {(mapa.descartados ?? []).length > 0 && (
            <Flex gap={2} mt={3} align="flex-start">
              <Box color="orange.400" mt="2px">
                <WarningCircle size={14} />
              </Box>
              <Text fontSize="11px" color="gray.600">
                A leitura apontou {mapa.descartados?.length} trecho(s) que não existem no documento
                e foram descartados. Se faltar algum dado da contratada acima, leia de novo.
              </Text>
            </Flex>
          )}

          <Flex gap={2} mt={4} wrap="wrap">
            <Button
              size="sm"
              colorScheme="green"
              isLoading={confirmar.isPending}
              isDisabled={manter.size === 0}
              onClick={async () => {
                try {
                  await confirmar.mutateAsync({ docId: t.id, campos: Array.from(manter) });
                  const tipo = tipos.find(x => x.valor === t.contrato_tipo)?.rotulo;
                  toast.success(
                    tipo
                      ? `Mapa confirmado. Os contratos de ${tipo.toLowerCase()} deste evento já podem ser gerados.`
                      : 'Mapa confirmado. Os contratos deste evento já podem ser gerados.',
                  );
                } catch (e) {
                  toast.error(mensagemDoErro(e, 'Não foi possível confirmar o mapa.'));
                }
              }}
            >
              {mapa.status === 'confirmado' ? 'Confirmar de novo' : 'Confirmar mapa'}
            </Button>
            <Button size="sm" variant="ghost" onClick={lerDeNovo} isLoading={ler.isPending}>
              Ler de novo
            </Button>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              isLoading={remover.isPending}
              onClick={removerTemplate}
            >
              Remover
            </Button>
          </Flex>
          {mapa.status === 'confirmado' && mapa.confirmado_em && (
            <Text fontSize="11px" color="gray.400" mt={2}>
              Confirmado{mapa.confirmado_por_nome ? ` por ${mapa.confirmado_por_nome}` : ''} em{' '}
              {new Date(mapa.confirmado_em).toLocaleString('pt-BR')}
            </Text>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * Template de contrato do EVENTO: definido uma vez, serve todos os fornecedores.
 *
 * O template é o .docx do advogado da contratante. A IA lê cada arquivo UMA vez
 * e marca o que muda a cada fornecedor (o lado da contratada, o comercial e o
 * escopo do Anexo); uma pessoa confere e confirma. Depois disso, gerar o
 * contrato de cada fornecedor não relê o template.
 *
 * Cada arquivo é de um TIPO de contrato (serviço, compra de produto, parceria de
 * alimentos e bebidas...): o contrato do fornecedor gera só com os do tipo dele.
 */
export default function TemplateDoEvento({ eventoId, isOpen, onClose, tipoInicial }: Props) {
  const { data } = useTemplateDoEvento(eventoId, isOpen);
  const { uploadDocumento } = useEventos();
  const { ler, invalidar } = useAcoesTemplate(eventoId);
  const { acompanhar } = useTarefa(() => invalidar());
  const [enviando, setEnviando] = useState(false);
  const [tipoNovo, setTipoNovo] = useState(tipoInicial ?? TIPO_CONTRATO_PADRAO);
  // Aberta pela tela de um contrato de parceria, o arquivo novo já sobe como parceria.
  useEffect(() => {
    if (isOpen) setTipoNovo(tipoInicial ?? TIPO_CONTRATO_PADRAO);
  }, [isOpen, tipoInicial]);
  const ref = useRef<HTMLInputElement>(null);
  const templates = comTipo(data?.data?.templates ?? []);
  const tipos = data?.data?.tipos ?? [];
  const grupos = agruparPorTipo(templates, tipos);

  const enviar = async (file?: File) => {
    if (!file) return;
    setEnviando(true);
    let docId: number;
    try {
      docId = (
        await uploadDocumento({
          eventoId,
          file,
          tipo: 'template_contrato',
          contratoTipo: tipoNovo,
        })
      ).data.id;
    } catch (e) {
      toast.error(mensagemDoErro(e, 'Não foi possível enviar o template.'));
      setEnviando(false);
      return;
    }
    invalidar();
    if (tipoNovo === TIPO_CONTRATO_ADITIVO) {
      toast.success('Modelo do termo aditivo salvo. O robô de eventos preenche; não precisa ler.');
      setEnviando(false);
      return;
    }
    // Enviado e lido são passos distintos: se a leitura não começar, o arquivo
    // já está no evento e "Ler de novo" resolve — a mensagem tem que dizer isso.
    try {
      acompanhar(await ler.mutateAsync(docId));
    } catch (e) {
      toast.error(
        mensagemDoErro(e, 'Template enviado, mas a leitura não começou. Use "Ler de novo".'),
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="16px">Template de contrato do evento</DrawerHeader>
        <DrawerBody>
          <Text fontSize="13px" color="gray.500" mb={4}>
            Cada arquivo é de um <b>tipo de contrato</b> (prestação de serviço, compra de produto,
            parceria de alimentos e bebidas...) e serve todos os fornecedores desse tipo neste
            evento. A IA lê cada um uma vez e marca o que muda de fornecedor para fornecedor; você
            confere e confirma. O resto do texto é do advogado da contratante e não é tocado.
          </Text>
          <input
            ref={ref}
            type="file"
            accept=".docx"
            hidden
            onChange={e => {
              enviar(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Flex mb={4} gap={2} align="center" wrap="wrap">
            <Text fontSize="12px" color="gray.600">
              Tipo de contrato
            </Text>
            <SelectTipoContrato
              tipos={tipos}
              value={tipoNovo}
              onChange={setTipoNovo}
              maxW="260px"
              isDisabled={enviando}
            />
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FileArrowUp size={16} />}
              isLoading={enviando}
              onClick={() => ref.current?.click()}
            >
              {templates.length ? 'Enviar mais um template (.docx)' : 'Enviar template (.docx)'}
            </Button>
          </Flex>
          {grupos.map(s => (
            <Box key={s.valor} mb={2}>
              <Flex align="center" gap={2} mb={2}>
                <Text fontSize="12px" fontWeight={700} color="gray.600" textTransform="uppercase">
                  {s.tipo?.rotulo ?? s.valor}
                </Text>
                {s.valor === TIPO_CONTRATO_ADITIVO ? (
                  <Badge colorScheme="green" textTransform="none" fontSize="10px">
                    preenchido pelo robô
                  </Badge>
                ) : (
                  s.tipo && (
                    <Badge
                      colorScheme={s.tipo.confirmado ? 'green' : 'orange'}
                      textTransform="none"
                      fontSize="10px"
                    >
                      {s.tipo.confirmado ? 'pronto para gerar' : 'confira e confirme'}
                    </Badge>
                  )
                )}
              </Flex>
              {s.valor === TIPO_CONTRATO_ADITIVO && s.arquivos.length > 1 && (
                <Text fontSize="12px" color="gray.500" mb={2}>
                  O robô usa o arquivo enviado por último; os outros podem ser removidos.
                </Text>
              )}
              {s.arquivos.map(t => (
                <Template key={t.id} t={t} eventoId={eventoId} tipos={tipos} />
              ))}
            </Box>
          ))}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
